import { useId, useRef, useState, type KeyboardEvent } from 'react'
import type {
  CreateActivityDisplay,
  CreateActivityDisplaySection,
  CreateActivityDisplaySummary,
} from '@llj/ai-service-interface/dist/entities/create-activity-display.interface'
import type { ConfirmCardBodyProps, ConfirmCardRenderer } from './ConfirmCard'
import { activitySettingUrl } from './activity-setting-link'

// 建立活動確認卡的內容（#1696）：摘要＋四分頁＋不支援提示；建立後收合成結果與設定頁連結。
// 🔴 只依 props 畫、不碰全域狀態，之後批次建立把多張卡並排滑動也不必改這裡。

/** 與預設不同的標記文字；顏色以外再加文字，色盲也分得出來 */
export const CHANGED_MARK = '與預設不同'

interface CreatedActivity {
  activityId: string
  activityName: string
  isPublic: boolean
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

// display 來自伺服器存下的 JSON，形狀不對就當沒有，不讓整張卡壞掉
const readDisplay = (display: unknown): CreateActivityDisplay | null =>
  (isObject(display) && isObject(display.summary) && Array.isArray(display.sections) ?
    display as unknown as CreateActivityDisplay : null)

const readCreated = (result: unknown): CreatedActivity | null =>
  (isObject(result) && typeof result.activityId === 'string' && result.activityId ? {
    activityId: result.activityId,
    activityName: typeof result.activityName === 'string' ? result.activityName : '',
    isPublic: result.isPublic === true,
  } : null)

function Summary({ summary }: { summary: CreateActivityDisplaySummary }) {
  return (
    <div className="activity-card-summary">
      {/* 是否公開最醒目：建立後就會被外人看到的事，要在按確認前一眼看清 */}
      <p className={`activity-card-public ${summary.isPublic ? 'public' : 'private'}`}>
        {summary.publicText}
      </p>
      <p className="activity-card-name">{summary.activityName}</p>
      <dl className="activity-card-facts">
        <div><dt>時間</dt><dd>{summary.startAt} ～ {summary.endAt}</dd></div>
        <div><dt>地點</dt><dd>{summary.location}</dd></div>
        <div><dt>名額</dt><dd>{summary.quotaText}</dd></div>
      </dl>
    </div>
  )
}

const changedCount = (section: CreateActivityDisplaySection): number =>
  section.fields.filter((item) => item.changed).length

// 四分頁：方向鍵、Home／End 切換，只有選中的分頁在 Tab 順序裡（WAI-ARIA tabs）
function SectionTabs({ sections }: { sections: CreateActivityDisplaySection[] }) {
  const baseId = useId()
  const [active, setActive] = useState(0)
  const tabs = useRef<(HTMLButtonElement | null)[]>([])
  const select = (index: number) => {
    const next = (index + sections.length) % sections.length
    setActive(next)
    tabs.current[next]?.focus()
  }
  const onKeyDown = (event: KeyboardEvent) => {
    const target = { ArrowRight: active + 1, ArrowLeft: active - 1, Home: 0, End: sections.length - 1 }[
      event.key as 'ArrowRight' | 'ArrowLeft' | 'Home' | 'End']
    if (target === undefined) return
    event.preventDefault()
    select(target)
  }
  const current = sections[active]
  return (
    <div className="activity-card-sections">
      <div className="activity-card-tabs" role="tablist" aria-label="活動設定" onKeyDown={onKeyDown}>
        {sections.map((section, index) => {
          const count = changedCount(section)
          return (
            <button
              key={section.key} ref={(node) => {
                tabs.current[index] = node
              }}
              type="button" role="tab" id={`${baseId}-tab-${index}`}
              aria-selected={index === active} aria-controls={`${baseId}-panel`}
              tabIndex={index === active ? 0 : -1} className="activity-card-tab"
              onClick={() => setActive(index)}
            >
              {section.title}
              {count > 0 && (
                <>
                  <span className="activity-card-tab-mark" aria-hidden="true">{count}</span>
                  <span className="sr-only">，{count} 項{CHANGED_MARK}</span>
                </>
              )}
            </button>
          )
        })}
      </div>
      {/* tabpanel 放在外層 div，dl 的 dt／dd 語意才不會被 role 蓋掉 */}
      <div
        className="activity-card-panel" role="tabpanel" id={`${baseId}-panel`}
        aria-labelledby={`${baseId}-tab-${active}`} tabIndex={0}
      >
        <dl className="activity-card-fields">
          {current.fields.map((item, index) => (
            <div key={index} className={`activity-card-field${item.changed ? ' changed' : ''}`}>
              <dt>{item.label}</dt>
              <dd>
                {item.value}
                {item.changed && <span className="activity-card-changed">✱ {CHANGED_MARK}</span>}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

// 建立後收合：最終名稱（重名時伺服器會加「(n)」）、公開狀態與設定頁連結
function Created({ created, fallbackName }: { created: CreatedActivity | null; fallbackName: string }) {
  return (
    <div className="activity-card-created">
      <p className="activity-card-name">{created?.activityName || fallbackName}</p>
      {created && (
        <>
          {/* 報名頁的網址 repo 內查不到規則，先請使用者到設定頁複製 */}
          <p className="activity-card-note">
            {created.isPublic ? '已公開，報名連結請到活動設定頁複製。' : '目前為未公開，需要時可到活動設定頁開啟。'}
          </p>
          <a
            className="activity-card-link" target="_blank" rel="noopener noreferrer"
            href={activitySettingUrl(window.location, created.activityId)}
          >
            前往活動設定
          </a>
        </>
      )}
    </div>
  )
}

export function CreateActivityBody({ display, result, status }: ConfirmCardBodyProps) {
  const data = readDisplay(display)
  if (status === 'executed') {
    return <Created created={readCreated(result)} fallbackName={data?.summary.activityName ?? ''} />
  }
  if (!data) return null
  // 取消、中斷、失敗只留摘要：看得出當初要建的是哪一場，不再攤開全部設定
  if (status !== 'pending' && status !== 'executing') return <Summary summary={data.summary} />
  return (
    <>
      <Summary summary={data.summary} />
      {data.sections.length > 0 && <SectionTabs sections={data.sections} />}
      {data.unsupported.length > 0 && (
        <p className="activity-card-unsupported">
          這裡還不能設定{data.unsupported.join('、')}，建立後請到活動設定頁補上。
        </p>
      )}
    </>
  )
}

export const CREATE_ACTIVITY_RENDERER: ConfirmCardRenderer = {
  Body: CreateActivityBody,
  labels: {
    confirm: '確認建立', cancel: '取消', executing: '建立中…',
    done: '已建立', notDone: '未建立', failed: '建立失敗',
  },
}
